import { useState } from "react";
import { Link } from "react-router";
import { useAngemeldet } from "../../kern/sitzung";
import { useAnsicht } from "../../kern/ansichten";
import { Wertwahl, type WertOption } from "../../kern/ui/Wertwahl";
import { usePersonen } from "../../kern/personen";
import { anzeigename } from "../../kern/namen";
import { heute, lang } from "../../kern/zeit";
import { fehlertext } from "../../kern/fehler";
import { ErsteSchritte } from "../../kern/ui/ErsteSchritte";
import { Karte } from "../../kern/ui/Karte";
import { Kachel, Kacheln } from "../../kern/ui/Kachel";
import { tagMitWochentag } from "../../kern/kalender";
import { NeueAufgabeKnopf } from "../../kern/ui/NeueAufgabeKnopf";
import { Leer, Meldung } from "../../kern/ui/Leer";
import seiten from "../../kern/seiten/seiten.module.css";
import { useAufgaben, useRessortFarben, useRessorts } from "./daten";
import { sortieren, type Aufgabe } from "./logik";
import { restText, sprintFortschritt } from "./sprintlogik";
import { laufenderSprint, useSitzungstermine, useSprints } from "./sprints";
import { useSchnellaktionen } from "./aktionen";
import { Rueckfrage } from "./Rueckfrage";
import { AufgabenListe, StatusGruppen } from "./Liste";
import { useLoeschen } from "./loeschen";
import { useVorhabenUebersicht } from "./vorhaben";
import { meineVorhaben, zeitraumKurz } from "./vorhabenlogik";
import { RESSORT_FLAECHEN } from "./listenlogik";
import { sitzungOber, sitzungStand, sprintStand, vorhabenOber, vorhabenStand } from "./kachellogik";
import s from "./aufgaben.module.css";
import { Laedt } from "../../kern/ui/Laedt";

type Gruppierung = "keine" | "status";
const GRUPPIERUNG: readonly WertOption<Gruppierung>[] = [
  { wert: "keine", text: "nicht", erklaerung: "Offene Aufgaben, Erledigtes darunter zum Aufklappen" },
  { wert: "status", text: "nach Status", erklaerung: "Zu tun, In Arbeit, Wartet, Erledigt" },
];

/** Ansicht 01: Startseite „Meine Woche“. Oben Kacheln (BAUPLAN-FEINSCHLIFF.md, Abschnitt 4.1): Sprint (Vorstand), nächste Sitzung und je
 *  eigenem Vorhaben, alle gleich aufgebaut, weiß und gleich hoch. Darunter als Tabellen in voller
 *  Breite (AUFGABEN-LISTE.md): meine Aufgaben im laufenden Sprint, meine Unteraufgaben in Aufgaben anderer, Vorstand: „Aus meinen
 *  Vorhaben“ (eigene ohne Sprint mit Vorhaben, die übrigen bleiben im Backlog), Mitglieder: mein Backlog, „Offen, wer übernimmt?“. */
export function Woche() {
  const sitzung = useAngemeldet();
  const ich = sitzung.person.id;
  const istVorstand = sitzung.rechte.has("aufgaben.vorstand_lesen");
  const darfPlanen = sitzung.rechte.has("sprint.planen");
  const darfLeiten = sitzung.rechte.has("sitzung.leiten");
  const aufgaben = useAufgaben();
  const sprints = useSprints();
  const termine = useSitzungstermine();
  const personen = usePersonen();
  const ressorts = useRessorts();
  const farben = useRessortFarben();
  const sa = useSchnellaktionen();
  const loeschen = useLoeschen(sa);
  const vorhaben = useVorhabenUebersicht();
  // Über „+ Aufgabe hinzufügen“ in „Offen, wer übernimmt?“ angelegt: bleibt sichtbar, auch wenn die Liste sonst nur die ersten fünf zeigt
  const [frischOffen, setFrischOffen] = useState<ReadonlySet<string>>(new Set());
  // „Meine Aufgaben“: Vorgabe nach Status, gemerkt je Person auf allen Geräten (BAUPLAN-STATUS-IN-LISTEN.md, Abschnitt 2 und 4)
  const [gruppe, setGruppe] = useAnsicht<Gruppierung>("woche.gruppierung", ["keine", "status"], "status");

  const tag = heute();
  const meineKapseln = meineVorhaben(vorhaben.data ?? []);
  const laufend = laufenderSprint(sprints.data);
  const alle = aufgaben.data ?? [];
  const nachId = new Map(alle.map((a) => [a.id, a]));
  const imSprint = (a: Aufgabe) => !!laufend && a.sprint_id === laufend.id;
  const meineImSprint = alle.filter((a) => !a.eltern_id && a.personen.includes(ich) && imSprint(a));
  const meineOffen = sortieren(meineImSprint.filter((a) => a.status !== "erledigt"));
  const meineErledigt = meineImSprint.filter((a) => a.status === "erledigt");
  const unterBeiAnderen = sortieren(
    alle.filter((a) => a.eltern_id && a.personen.includes(ich) && a.status !== "erledigt" && imSprint(a) && !nachId.get(a.eltern_id)?.personen.includes(ich)),
  );
  const meinBacklog = sortieren(alle.filter((a) => a.personen.includes(ich) && !a.sprint_id && a.status !== "erledigt"));
  // Vorstand (BAUPLAN-PAKETE.md, Abschnitt 9.4): eigene ohne Sprint, die zu einem Vorhaben gehören. Die übrigen bleiben im Backlog.
  const ausVorhaben = meinBacklog.filter((a) => !!a.vorhaben_id);
  const offen = sortieren(alle.filter((a) => a.personen.length === 0 && a.status !== "erledigt" && a.sprint_status !== "abgeschlossen"));
  const offenKurz = offen.filter((a, i) => i < 5 || frischOffen.has(a.id));
  const ueberfaellig = [...meineOffen, ...unterBeiAnderen].filter((a) => a.ueberfaellig).length;
  const sprintAufgaben = alle.filter((a) => !a.eltern_id && imSprint(a));
  const sprint = sprintStand(sprintFortschritt(sprintAufgaben));
  const naechsteSitzung = (termine.data ?? []).find((t) => (t.status === "geplant" || t.status === "verschoben") && t.datum >= tag);
  const unter = new Map<string, Aufgabe[]>();
  for (const a of alle) if (a.eltern_id) unter.set(a.eltern_id, [...(unter.get(a.eltern_id) ?? []), a]);
  const gemeinsam = {
    personen: personen.data,
    ressorts: ressorts.data,
    farben: farben.data,
    sa,
    loeschen,
    unterVon: (id: string) => unter.get(id) ?? [],
    sprint: laufend ? { beginn: laufend.beginn, ende: laufend.ende, name: laufend.name } : null,
  };

  const offenAbschnitt = (
    <section className={s.abschnitt} aria-labelledby="offen-titel">
      <div className={s.abschnittKopf}>
        <h2 id="offen-titel">{istVorstand ? "Offen, wer übernimmt?" : "Wer hilft?"}</h2>
        <span className={s.meta}>Noch niemand verantwortlich. Ein Klick, dann liegt sie bei dir.</span>
      </div>
      {offen.length === 0 ? (
        <p className={s.meta}>Alles hat eine Person.</p>
      ) : (
        <AufgabenListe
          liste={offenKurz}
          label={istVorstand ? "Offen, wer übernimmt?" : "Wer hilft?"}
          zeigeOrt
          onUebernehmen={sa.uebernehmen}
          neu={{ vorgabe: { person: "offen" }, onAngelegt: (id) => setFrischOffen((alt) => new Set(alt).add(id)) }}
          {...gemeinsam}
        />
      )}
      {offen.length > offenKurz.length ? <Link to="/offen">Alle {offen.length} offenen ansehen</Link> : null}
    </section>
  );

  return (
    <div className={seiten.stapel}>
      <div className={seiten.kopf}>
        <div>
          <h1>Meine Woche</h1>
          <p>
            Hallo {sitzung.person.vorname || anzeigename(sitzung.person)}. {lang(tag)} ·{" "}
            {meineOffen.length === 1 ? "1 Aufgabe" : `${meineOffen.length} Aufgaben`} im Sprint bei dir
            {ueberfaellig > 0 ? `, ${ueberfaellig} überfällig` : ""}.
          </p>
        </div>
        <NeueAufgabeKnopf />
      </div>
      {aufgaben.isError ? <Meldung fehler>{fehlertext(aufgaben.error)}</Meldung> : null}
      {aufgaben.data ? <ErsteSchritte aufgabe={alle.some((a) => a.personen.includes(ich) && !a.archiviert_am)} /> : null}
      <Kacheln label="Überblick">
        {istVorstand && laufend ? (
          <Kachel
            key="sprint"
            ober={`Sprint · ${restText(laufend, tag)}`}
            haupt={laufend.name}
            stand={
              <>
                {sprint.zahl ? <b>{sprint.zahl}</b> : null} {sprint.text}
              </>
            }
            balken={sprint.prozent === null ? undefined : [{ anteil: sprint.prozent, farbe: "var(--black)" }]}
            link={darfPlanen ? { to: "/sprint", label: `Zum ${laufend.name}`, weiter: "Zum Sprint" } : undefined}
          />
        ) : null}
        {naechsteSitzung ? (
          // Ganze Kachel führt zur Sitzung, aber nur mit sitzung.leiten (die Seite /sitzung braucht das Recht). Sonst ohne Fuß.
          <Kachel
            key="sitzung"
            ober={sitzungOber(naechsteSitzung.datum, tag)}
            haupt={`${tagMitWochentag(naechsteSitzung.datum)}${naechsteSitzung.datum === tag ? " (heute)" : ""}`}
            stand={sitzungStand(naechsteSitzung)}
            link={darfLeiten ? { to: "/sitzung", label: `Zur nächsten Sitzung am ${lang(naechsteSitzung.datum)}`, weiter: "Zur Sitzung" } : undefined}
          />
        ) : null}
        {meineKapseln.map((v) => {
          const st = vorhabenStand(v, tag);
          const i = v.ressort_id ? farben.data?.get(v.ressort_id) : undefined;
          return (
            <Kachel
              key={v.id}
              punkt={(i === undefined ? null : RESSORT_FLAECHEN[i]) ?? "var(--dashed)"}
              ober={vorhabenOber(v.meine_rolle)}
              haupt={v.name}
              stand={
                <>
                  {st.vor}
                  {st.zahl ? <b>{st.zahl}</b> : null}
                  {st.nach}
                </>
              }
              balken={st.prozent === null ? undefined : [{ anteil: st.prozent, farbe: "var(--black)" }]}
              link={{ to: `/vorhaben/${v.id}`, label: `${v.name}, ${zeitraumKurz(v, tag)}, öffnen`, weiter: "Öffnen" }}
            />
          );
        })}
      </Kacheln>
      <section className={s.abschnitt} aria-labelledby="meine-titel">
        <div className={s.abschnittKopf}>
          <h2 id="meine-titel">{laufend ? `Meine Aufgaben in ${laufend.name}` : "Meine Aufgaben"}</h2>
          <span className={s.abschnittRechts}>
            <span className={s.meta}>
              {meineOffen.length} offen · {meineErledigt.length} erledigt
            </span>
            <Wertwahl art="Gruppieren" vorsatz="Gruppieren:" optionen={GRUPPIERUNG} wert={gruppe} klein onWechsel={setGruppe} />
          </span>
        </div>
        {aufgaben.isPending ? <Laedt /> : null}
        {aufgaben.data && meineOffen.length === 0 ? (
          <Karte>
            <Leer titel="Nichts offen im Sprint.">
              Unter <Link to="/offen">Offen</Link> findest du, wo Hilfe gebraucht wird.
            </Leer>
          </Karte>
        ) : null}
        {gruppe === "status" && meineImSprint.length > 0 ? (
          // Nach Status: die Gruppe „Erledigt“ (zu) ersetzt „Erledigt in diesem Sprint“
          <div className={s.gruppen}>
            <StatusGruppen
              liste={[...meineOffen, ...sortieren(meineErledigt)]}
              label={laufend ? `Meine Aufgaben in ${laufend.name}` : "Meine Aufgaben"}
              neu={darfPlanen && laufend ? { vorgabe: { wo: "sprint" } } : undefined}
              {...gemeinsam}
            />
          </div>
        ) : null}
        {gruppe === "keine" && meineOffen.length > 0 ? (
          <AufgabenListe
            liste={meineOffen}
            label={laufend ? `Meine Aufgaben in ${laufend.name}` : "Meine Aufgaben"}
            // Plus-Zeile nur, wenn die neue Aufgabe hier landet: ohne sprint.planen setzt die Datenbank sie in den Backlog
            neu={darfPlanen && laufend ? { vorgabe: { wo: "sprint" } } : undefined}
            {...gemeinsam}
          />
        ) : null}
        {gruppe === "keine" && meineErledigt.length > 0 ? (
          <details className={s.ausklapp}>
            <summary>Erledigt in diesem Sprint · {meineErledigt.length}</summary>
            <AufgabenListe liste={meineErledigt} label="Erledigt in diesem Sprint" {...gemeinsam} />
          </details>
        ) : null}
      </section>
      {unterBeiAnderen.length > 0 ? (
        <section className={s.abschnitt} aria-labelledby="unter-titel">
          <div className={s.abschnittKopf}>
            <h2 id="unter-titel">Meine Unteraufgaben in Aufgaben anderer</h2>
            <span className={s.meta}>{unterBeiAnderen.length} offen</span>
          </div>
          <AufgabenListe liste={unterBeiAnderen} label="Meine Unteraufgaben in Aufgaben anderer" zeigeEltern {...gemeinsam} unterVon={undefined} />
        </section>
      ) : null}
      {istVorstand && ausVorhaben.length > 0 ? (
        <section className={s.abschnitt} aria-labelledby="aus-vorhaben-titel">
          <div className={s.abschnittKopf}>
            <h2 id="aus-vorhaben-titel">Aus meinen Vorhaben</h2>
            <span className={s.meta}>Bei dir, noch nicht im Sprint.</span>
          </div>
          <AufgabenListe liste={ausVorhaben} label="Aus meinen Vorhaben" zeigeEltern {...gemeinsam} />
        </section>
      ) : null}
      {!istVorstand ? (
        <section className={s.abschnitt} aria-labelledby="backlog-titel">
          <div className={s.abschnittKopf}>
            <h2 id="backlog-titel">Mein Backlog</h2>
            <span className={s.meta}>Noch ohne Sprint. Der Vorstand holt Aufgaben in den Sprint.</span>
          </div>
          {meinBacklog.length === 0 ? (
            <p className={s.meta}>Nichts im Backlog.</p>
          ) : (
            <AufgabenListe liste={meinBacklog} label="Mein Backlog" zeigeEltern neu={{ vorgabe: { wo: "backlog" } }} {...gemeinsam} />
          )}
        </section>
      ) : null}
      {offenAbschnitt}
      <Rueckfrage text={sa.rueckfrage?.text ?? null} onAntwort={(mit) => sa.rueckfrageAntwort(mit)} onAbbrechen={sa.rueckfrageSchliessen} />
      {loeschen.rueckfrage}
    </div>
  );
}
